import { Observable, Subject } from 'rxjs';
import { decodeJwt } from './decode-jwt';
import Axios from 'axios-observable';
import { TokenExpired } from './token-expired';
import { AxiosRequestConfig } from 'axios';
import { isAxiosError } from './util';
import { tap } from 'rxjs/operators';
import { cookieStorage } from '../cookie-storage';
import { Cognito } from '@api/auth/cognito.types';

export class TokenManager {
  /**
   * Prefix the storge keys.
   */
  static PREFIX = 'vtr';
  /**
   * The name of the access key.
   */
  static ACCESS = `${TokenManager.PREFIX}_access_token`;
  /**
   * The name of the refresh key.
   */
  static REFRESH = `${TokenManager.PREFIX}_refresh_token`;
  /**
   * The name of the ID token key.
   */
  static ID = `${TokenManager.PREFIX}_id_token`;

  static MAX_REFRESH_RETRIES = 5;
  protected storage: Storage;

  constructor(storage: Storage = null) {
    if (!storage) this.resolveStorage();
  }

  protected resolveStorage() {
    const storages: Storage[] = [cookieStorage];
    if (typeof window !== 'undefined') {
      storages.push(localStorage);
    }
    for (const storage of storages) {
      this.storage = storage;
      if (this.exists()) break;
    }
  }

  init() {
    if (this.isAccessTokenValid()) {
      this.onTokenSet();
    } else {
      this.clearTokens();
    }
  }

  /**
   * Switch the storage used for token.
   * @param storage The storage type to switch
   * @param skipEvents Update the storage without firing events
   */
  switchStorage(storage: Storage, skipEvents = false) {
    if (storage === this.storage) return;
    if (this.exists()) {
      const { access, refresh, id } = this.getToken();
      this.storage = storage;
      if (skipEvents) {
        this.setAccessToken(access);
        this.setRefreshToken(refresh);
        this.setIdToken(id);
      } else this.setTokens(access, refresh, id);
    } else {
      this.storage = storage;
    }
  }

  protected loginApi: LoginApi;
  protected refresher: TokenRefresher;

  setLoginApi(api: LoginApi) {
    this.loginApi = api;
  }

  /**
   * Login a user from API.
   * @param username The username
   * @param passwd The password
   */
  login(username: string, passwd: string) {
    return this.loginApi(username, passwd).pipe(
      tap(({ access_token, refresh_token, id_token }) =>
        this.setTokens(access_token, refresh_token, id_token)
      )
    );
  }

  /**
   * Logout the current user.
   */
  logout() {
    this.clearTokens();
  }

  /**
   * Internal api for reload functionaly.
   */
  protected refreshing: Promise<boolean>;
  event$ = new Subject<TokenEvents>();
  /**
   * The access token of this session.
   */
  get access(): string {
    return this.getToken('access');
  }
  protected setAccessToken(value: string) {
    this.storage.setItem(TokenManager.ACCESS, value);
  }
  /**
   * The refresh token of this session.
   */
  get refresh() {
    return this.getToken('refresh');
  }
  protected setRefreshToken(value: string) {
    this.storage.setItem(TokenManager.REFRESH, value);
  }

  get id() {
    return this.getToken('id');
  }
  protected setIdToken(value: string) {
    this.storage.setItem(TokenManager.ID, value);
  }

  /**
   * Set the token refresher.
   * @param refresher A function running API call to refresh the token.
   */
  public setRefresher(refresher: TokenRefresher) {
    this.refresher = refresher;
    return this;
  }

  onTokenInvalidated() {
    this.event$.next(TokenEvents.CLEAR_TOKEN);
  }

  onTokenSet() {
    this.event$.next(TokenEvents.SET_TOKEN);
  }

  /**
   * Call the API function to reload the token.
   */
  protected async reloadToken() {
    try {
      const { access_token, refresh_token, id_token } = await this.refresher(
        this.refresh
      ).toPromise();
      this.setTokens(access_token, refresh_token, id_token);
    } catch (err) {
      throw err;
    }
  }

  /**
   * Check if the access token is valid.
   */
  isAccessTokenValid() {
    if (this.hasAccess()) {
      try {
        return !this.isExpired();
      } catch {}
    }
    return false;
  }

  /**
   * Set the header on the instance.
   * @param request The request configuration of axios
   */
  setHeader(request: AxiosRequestConfig) {
    request.headers.Authorization = `Bearer ${this.id}`;
    request.headers['X-vtr-auth-at'] = this.access;
    request.headers['X-vtr-auth-src'] = 'cognito';
  }

  /**
   * Flag to retain Check only once, across multiple requests.
   */
  protected checking: Promise<any>;
  /**
   * Check if the token is valid or invalid.
   */
  protected async checkToken() {
    if (!this.checking) {
      this.checking = this.verifyOrInvalidate();
    }
    await this.checking;
    this.checking = null;
  }

  /**
   * Try to reload the access token via the refresh token,
   * only if the refresh token exists in the storage.
   * @returns True if refreshed, false if not refreshed.
   */
  protected async refreshTokenIfPossible() {
    if (this.hasRefresh()) {
      return await this.reloadToken()
        .then(() => true)
        .catch(() => false);
    }
    return false;
  }

  /**
   * Check if the token is valid, or,
   * clear if invalid.
   */
  protected async verifyOrInvalidate() {
    const accessTokenValid = this.hasAccess() && this.isAccessTokenValid();

    if (!accessTokenValid && !(await this.refreshTokenIfPossible())) {
      this.clearTokens();
      return false;
    }
    return true;
  }

  configureHttp(instance: Axios, errorModel = error => ({ data: null, error, success: false })) {
    // Request

    instance.interceptors.request.use(async (request: AxiosRequestConfig) => {
      await this.checkToken();
      if (this.exists()) {
        this.setHeader(request);
        return request;
      }
      throw new TokenExpired(request);
    });

    // Response
    instance.interceptors.response.use(
      r => r,
      async (error: any) => {
        if (error instanceof TokenExpired) {
          // Token expired, cannot refresh, request was canceled.
          // Generate the error model
          return Promise.reject(errorModel(error));
        }

        if (isAxiosError(error) && error?.response?.status === 401) {
          // Unauthorized error. access token is invalid. Clear token and re-try the request.

          // explicitly invalidate token.
          this.setAccessToken('');

          /**
           * Create a counter config to break infinite
           * access token refreshes.
           */
          const counterErrorConfig = {
            ...error.config,
            __counter: error.config['__counter'] ? error.config['__counter'] + 1 : 1,
          };

          if (counterErrorConfig.__counter >= TokenManager.MAX_REFRESH_RETRIES) {
            console.warn('Maximum refresh token retries exceeded!');
            // Clear the tokens to avoid infinite loop and
            // logout the user.
            this.clearTokens();
            return Promise.reject(errorModel(new TokenExpired(error.config)));
          }

          // Retry the request
          return instance.request(counterErrorConfig).toPromise();
        }

        return Promise.reject(error);
      }
    );

    return instance;
  }

  /**
   * Get Access and Refresh Token.
   */
  getToken(): { access: string; refresh: string; id: string };
  /**
   * Get The specified `type` of token.
   * @param type The type of token
   */
  getToken(type: 'access' | 'refresh' | 'id'): string;
  getToken(type?: 'access' | 'refresh' | 'id') {
    if (type) {
      return this.storage.getItem(`${TokenManager.PREFIX}_${type}_token`);
    }
    return {
      access: this.storage.getItem(TokenManager.ACCESS),
      refresh: this.storage.getItem(TokenManager.REFRESH),
      id: this.storage.getItem(TokenManager.ID),
    };
  }

  /**
   * Check if the token is expired. Locally.
   * @param now The date to check against.
   *
   * @ref https://github.com/auth0/jwt-decode/issues/53#issuecomment-413069022
   */
  isExpired(now = Date.now().valueOf() / 1000): boolean {
    const decoded = decodeJwt<Cognito.AccessPayload>(this.access);
    if (typeof decoded.exp !== 'undefined' && decoded.exp < now) {
      // The token is expired.
      return true;
    }
    if (typeof decoded.nbf !== 'undefined' && decoded.nbf > now) {
      // Token is not yet valid. It will be valid in future.
      return true;
    }
    return false;
  }

  /**
   * Check if this token exists in the local storage.
   */
  exists(): boolean {
    return !!(this.access && this.refresh && this.id);
  }

  hasAccess() {
    return !!this.access;
  }

  /**
   * Check if the reload token is valid.
   */
  hasRefresh() {
    return !!this.refresh;
  }

  hasId() {
    return !!this.id;
  }

  /**
   * Save the tokens to the local storage.
   * @param access The access token string.
   * @param refresh The refresh token string.
   */
  setTokens(access: string, refresh: string, id: string) {
    this.setAccessToken(access);
    this.setRefreshToken(refresh);
    this.setIdToken(id);
    this.onTokenSet();
    return this;
  }

  /**
   * Clear the tokens and local session.
   */
  clearTokens() {
    this.storage.removeItem(TokenManager.ACCESS);
    this.storage.removeItem(TokenManager.REFRESH);
    this.storage.removeItem(TokenManager.ID);
    this.onTokenInvalidated();
    return this;
  }

  /**
   * A promise to reload the token from a API/service and wait
   * until the api call is finished.
   */
  async reload() {
    if (!this.refreshing) {
      this.refreshing = this.reloadToken().then(() => (this.refreshing = null));
    }
    await this.refreshing;
  }
}
type TokenRefresher = (
  refreshToken: string
) => Observable<{ access_token: string; refresh_token: string; id_token: string }>;

type LoginApi = (
  username: string,
  password: string
) => Observable<{ access_token: string; refresh_token: string; id_token: string }>;

export enum TokenEvents {
  SET_TOKEN,
  CLEAR_TOKEN,
}
